






























































































































































import Vue, { PropType } from 'vue';

import * as Utils from '@/utils';
import { PeerGroupMember } from '@/models/orgStructure';
import {
  WebRequestStatus,
  BaseUser,
  MemberRole,
  BasePeerGroup
} from '@/models';

import UnrulrFilter from '@/components/Filter.vue';
import LazyRenderingList from '@/components/LazyRenderingList.vue';
import OrganizationUserCreate from '@/components/OrganizationUserCreate.vue';
import OrganizationUserCreateMultiple from '@/components/OrganizationUserCreateMultiple.vue';
import OrganizationPeerGroupMember from '@/components/OrganizationPeerGroupMember.vue';
import PeerGroupRoleInfo from '@/components/info/PeerGroupRoleInfo.vue';
import PeerGroupRoleSelectorPills from '@/components/PeerGroupRoleSelectorPills.vue';

type ListMember = [PeerGroupMember, WebRequestStatus, boolean];

export default Vue.extend({
  components: {
    UnrulrFilter,
    LazyRenderingList,
    OrganizationUserCreate,
    OrganizationUserCreateMultiple,
    OrganizationPeerGroupMember,
    PeerGroupRoleInfo,
    PeerGroupRoleSelectorPills
  },

  props: {
    members: {
      type: Array as PropType<[PeerGroupMember, WebRequestStatus][]>,
      required: true
    },

    nonMembers: {
      type: Array as PropType<[PeerGroupMember, WebRequestStatus][]>,
      required: true
    },

    membersFirst: {
      type: Boolean,
      default: true
    },

    peerGroup: {
      type: Object as PropType<BasePeerGroup>,
      required: true
    }
  },

  data() {
    return {
      rawFilter: '',
      inputFocused: false,
      multiRole: MemberRole.STUDENT
    };
  },

  computed: {
    filter(): string {
      return Utils.trimBrackets(this.rawFilter.trim());
    },

    filteredMembers(): ListMember[] {
      return Utils.findTerm(this.filter, this.members, ([m]) => [
        m.name,
        m.emails.map(ue => ue.email).join(' ')
      ]).map(([m, status]) => [m, status, true]);
    },

    newMemberOptions(): ListMember[] {
      if (!this.filter) {
        if (this.membersFirst) {
          return [];
        }

        return this.nonMembers.map(([m, status]) => [m, status, false]);
      }

      return Utils.findTerm(this.filter, this.nonMembers, ([m]) => [
        m.name,
        m.emails.map(ue => ue.email).join(' ')
      ]).map(([m, status]) => [m, status, false]);
    },

    listed(): ListMember[] {
      if (this.membersFirst) {
        return [...this.filteredMembers, ...this.newMemberOptions];
      }

      return [...this.newMemberOptions, ...this.filteredMembers];
    },

    exactMatch(): boolean {
      if (!this.filter) {
        return false;
      }

      return false;
    },

    showUserCreate(): boolean {
      if (!this.filter) {
        return false;
      }

      if (this.showMultiUserCreate) {
        return false;
      }

      if (this.listed.length === 0) {
        return true;
      }

      const noExactMatch = this.listed
        .flatMap(([lm]) => lm.emails)
        .every(userEmail => userEmail.email !== this.filter);

      // Filter does not match any existing emails
      return noExactMatch;
    },

    showMultiUserCreate(): boolean {
      const tokenCount = Utils.parseByDelimiter(this.filter).length;

      return tokenCount > 1;
    },

    multiCreateBlockingUsers(): PeerGroupMember[] {
      return this.members.map(([m]) => m);
    },

    multiCreateAllowedUsers(): BaseUser[] {
      return this.nonMembers.map(([nm]) => ({ ...nm, role: undefined }));
    },

    placeholderText(): string {
      return 'Type to find or add a member';
    }
  },

  methods: {
    updateFilter(value: string) {
      this.rawFilter = value;
    },

    changeFocus(focused: boolean) {
      this.inputFocused = focused;
    },

    validateAndCreateUser(email: string) {
      if (!Utils.validateEmail(email)) {
        return;
      }

      this.$emit('create-member', { email, role: MemberRole.STUDENT });
    },

    addMultiUser(newUser: string | BaseUser) {
      if (typeof newUser === 'string') {
        this.$emit('create-member', { email: newUser, role: this.multiRole });
      } else {
        this.$emit('add-member', { ...newUser, role: this.multiRole });
      }
    },

    addMultiUsers(values: (string | PeerGroupMember)[]) {
      const emails: string[] = [];
      const users: PeerGroupMember[] = [];

      for (const v of values) {
        if (typeof v === 'string') {
          emails.push(v);
        } else {
          users.push({ ...v, role: this.multiRole });
        }
      }

      this.$emit('add-members', users);
      this.$emit('create-members', { emails, role: this.multiRole });
    },

    changeRole(member: PeerGroupMember) {
      this.$emit('change-role', member);
    },

    addMember(member: PeerGroupMember) {
      this.$emit('add-member', member);
    },

    removeMember(member: PeerGroupMember) {
      this.$emit('remove-member', member);
    },

    clearFilter() {
      this.filter = '';
    },

    handleRoleChange(role: MemberRole) {
      this.multiRole = role;
    }
  }
});
